import React from 'react';
import { isEmpty, get, filter, isUndefined, map } from 'lodash';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';

// local dependencies
import { SERVICE_RESPONSE_STATUS, CALL_ACTIVITY_STATE } from 'constants/common';
import { dashboard as DashboardView } from 'ui/views/layouts/dashboard';
import { DashboardService } from 'services/dashboard';
import { WIDGET_NAMES } from 'constants/entity-names';
import { REDUCER } from 'constants/entity-names';
import { updateCallState } from 'store/actions/callState';

/**
 * @type {class}
 * @desc A React container/smart component to render a React presentational/dumb component (view).
 * @example
 * import { Dashboard } from 'ui/containers/Dashboard';
 *
 * // render
 * <Dashboard propName='propValue'/>
 */
class _Dashboard extends React.Component {
    
    /**
     * @param {object} props - props
     */
    constructor( props ) {
        super( props );

        // component state
        this.state = {
            isFetching: true,
            networkStatus: undefined,
            componentData: undefined,
        };

        // create service instance
        this.dashboardService = new DashboardService();
        
        // start component data fetch
        this.fetchComponentData();
    }

    /*********************************************/

    /**
     * @desc Fetch component response.
     */
    async fetchComponentData() {
        log.debug( 'Dashboard.fetchComponentData()' );

        // cancel ongoing request
        this.dashboardService.cancel();

        // get status and service data
        const { status, data } = await this.dashboardService.fetchWidgets();

        // check if response status was success
        if( SERVICE_RESPONSE_STATUS.SUCCESS !== status ) {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.FAILURE
            } );
        } else {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
                componentData: data,
            } );
        }
    }

    /**
     * @desc Get active widgets based on route.
     */
    getActiveWidgets( { widgets } = {} ) {

        // if `widgets` is empty, return an empty array
        if( isEmpty( widgets ) ) {
            return [];
        }

        /*--------------*/

        // get current URL route parameter (path)
        const activePath = get( this.props, 'match.params.widget' );

        // get visible widgets
        const visibleWidgets = filter( widgets, ( widget ) => {

            // always include call-control widget
            // if - MSR is on the `landing route`
            // if - MSR on the route other than landing route and call is ongoin
            if( widget.name === WIDGET_NAMES.CALL_CONTROL ) {
                if( isEmpty( activePath ) ) {
                    return true; // display: on landing route
                } else if( ! isEmpty( this.props.callState ) ) {
                    return true; // display: other route + ongoing call
                }
                
                return false;
            }

            /*--------*/

            // if `activePath` is undefined, only include `call-management lists` widget
            if( isUndefined( activePath ) ) {
                if( widget.name === WIDGET_NAMES.CALL_MANAGEMENT_LISTS ) {
                    return true;
                }

                return false;
            }

            // if `activePath` is defined, and it is only member-management then include `off-call-control` widget
            if( ! isUndefined( activePath ) ) {
                if( widget.name === WIDGET_NAMES.OFF_CALL_CONTROL && 'member-management' === activePath && isEmpty( this.props.callState ) ) {
                    return true;
                }
            }

            /*--------*/

            // include widget whose `path` matchs the `activePath`
            return activePath === widget.path;

        } );

        /*--------------*/

        // format widgets
        return map( visibleWidgets, visibleWidget => {

            // if call has ended, all widgets except `call control` should be inactive
            if( ( CALL_ACTIVITY_STATE.ENDED === get( this.props, 'callState.callState' ) || get( this.props, 'callState.callTransferred', false ) ) && visibleWidget.name !== WIDGET_NAMES.CALL_CONTROL ) {
                return {
                    ...visibleWidget,
                    inactive: true,
                };
            }

            // return widget as is
            return visibleWidget;
        } );
    }

    /*********************************************/

    /**
     * @desc When component unmounts, cleanup resouces.
     */
    componentWillUnmount() {
        this.dashboardService.cancel(); // cancel ongoing requests
    }

    /**
     * @desc Render a React view component
     */
    render() {
        return (
            <DashboardView
                isFetching={ this.state.isFetching }
                networkStatus={ this.state.networkStatus }
                widgets={ this.getActiveWidgets( this.state.componentData ) }
                history={this.props.history}
            />
        );
    }
}

// set display name
_Dashboard.displayName = '_Dashboard';

// set default props
_Dashboard.defaultProps = {};

// prop types
_Dashboard.propTypes = {};


/*************************************************/

// map state to props
const mapStateToProps = ( state ) => {
    return {
        callState: state[ REDUCER.CALL_STATE ],
        appContext: state[ REDUCER.APP_CONTEXT ],
    };
};

// map dispatch to props
const mapDispatchToProps = ( dispatch ) => {
    return {
        updateCallState: bindActionCreators( updateCallState, dispatch ),
    };
};

// return component with acccess to Redux store
export const Dashboard = connect( mapStateToProps, mapDispatchToProps )( _Dashboard );
